/**
 * camera.js — живой перевод через камеру для /photo-translate.
 *
 * Зачем это вообще есть. Wordstat, сентябрь 2026: «переводчик по фото» —
 * 2 114 035 показов в месяц, и внутри этого спроса запросы, называющие КАМЕРУ,
 * дают ещё около 270 000 («переводчик по фото с камеры» 52 412, «через камеру»
 * 51 745, «на камеру русский по фото» 52 191, «с камеры телефона» 51 316,
 * «через камеру бесплатно» 22 287, «онлайн через камеру» 19 371, «камера
 * переводчик по фото онлайн бесплатно» 17 330). До этого модуля тул отвечал на
 * них кнопкой `capture`, то есть системным снимком, — а человек ищет то, что
 * делает Google Lens: навёл, и надписи читаются на его языке прямо в кадре.
 *
 * Что здесь принципиально иначе, чем у камерных переводчиков-приложений:
 * кадр можно СНЯТЬ, и он уходит в обычный редактор тула — с правкой, выбором
 * языка и сохранением файла. Живой перевод у них есть, сохраняемого результата
 * нет; у нас теперь есть и то, и другое.
 *
 * Решения, которые взяты из измеримых свойств, а не из вкуса:
 *
 *  • Непрерывный проход — НЕ покадровый. PP-OCRv5 на WebGPU читает кадр за
 *    ~131 мс, на wasm за ~288 мс (замер корпуса, config/ocr-languages.php), и
 *    это только распознавание: к нему добавляется перевод новых строк. Поэтому
 *    цикл идёт «как успевает», а не по таймеру, и НИКОГДА не запускает второй
 *    проход поверх незаконченного.
 *  • Медленное устройство не обязано греться впустую. Если проход вышел
 *    дороже SLOW_MS, непрерывный режим выключается САМ и превращается в кнопку
 *    «Сканировать»: честное «здесь это дорого» лучше, чем подвисший кадр.
 *  • Перевод кэшируется по исходной строке. В живом кадре одни и те же надписи
 *    возвращаются каждый проход, и без кэша каждая секунда была бы новым
 *    запросом к переводчику на тот же текст.
 *  • Язык источника при «определить автоматически» определяется ОДИН раз, по
 *    первому непустому проходу, и дальше держится. Менять распознаватель на
 *    каждом кадре нельзя: смена языка закрывает воркер, которым идёт чтение.
 *  • Отказ камеры показывается ВНУТРИ видоискателя. Классификация и текст —
 *    из CameraManager: «нет доступа», «камеру держит другое приложение» и
 *    «камера не найдена» требуют разных действий, и один текст на все причины
 *    отправляет человека чинить не то.
 *
 * Экспортируется как window.PTCamera.
 */
(function (root) {
    'use strict';
    if (root.PTCamera) return;

    // Дороже этого один проход — и непрерывный режим не имеет смысла: кадр
    // успевает смениться раньше, чем на нём появится перевод.
    var SLOW_MS = 2600;
    // Ширина кадра, который уходит распознавателю. Детектор всё равно работает
    // на 960 px, поэтому больше — это только лишняя перекачка пикселей.
    var SCAN_W = 960;
    // Кэш переводов живёт на сессию камеры: надписи в кадре повторяются.
    var MAX_CACHE = 400;
    // Телефон ли это — на событиях с временем прохода: цену распознавания на
    // телефоне знает только поле (троттлинг CPU через CDP до воркера не доходит).
    var MOB = /Mobi|Android|iPhone|iPad/i.test((root.navigator && navigator.userAgent) || '') ? 1 : 0;

    function create(o) {
        var video = o.video, overlay = o.overlay, host = o.host;
        var cam = null, stream = null;
        var running = false, looping = false, stopped = true;
        var auto = true;                 // непрерывный проход
        var lockedLang = null;           // язык, определённый один раз
        var probing = null;              // иностранная письменность, которую проверяет этот проход
        var cache = new Map();
        var ctl = null;                  // AbortController перевода
        var lastBoxes = [];
        var lastFrame = { w: SCAN_W, h: SCAN_W };
        var passN = 0;
        var facing = 'environment';
        var octx = overlay ? overlay.getContext('2d') : null;

        function t(k, d) { return (o.i18n && o.i18n[k]) || d || k; }
        function funnel(s, d) { try { if (root.__aeFunnel) root.__aeFunnel(s, d || {}); } catch (_) { } }

        function setState(kind, text) {
            if (o.app) o.app.dataset.cam = kind;
            if (o.stateEl) o.stateEl.textContent = text || '';
        }
        function showFail(msg) {
            setState('fail', t('cam_none', ''));
            if (!o.failEl) return;
            o.failEl.textContent = msg;
            o.failEl.hidden = false;
        }
        function clearFail() { if (o.failEl) { o.failEl.hidden = true; o.failEl.textContent = ''; } }

        // ── запуск ───────────────────────────────────────────────────────

        async function start() {
            stopped = false;
            clearFail();
            setState('warm', t('cam_warm', 'Включаю камеру'));
            if (typeof root.CameraManager !== 'function') {
                showFail(t('cam_fail_module', 'Модуль камеры не загрузился — обновите страницу'));
                return false;
            }
            cam = new root.CameraManager({ slug: 'photo-translate' });
            try {
                stream = await cam.start({ facingMode: facing, width: 1280, height: 720 });
            } catch (e) {
                var code = (e && e.code) || (root.CameraManager.classify ? root.CameraManager.classify(e) : 'generic');
                var msg = '';
                try { msg = cam.getErrorMessage(code); } catch (_) { }
                showFail(msg || t('cam_fail', 'Камеру открыть не удалось'));
                funnel('camera_failed', { code: code, cause: root.CameraManager.causeName ? root.CameraManager.causeName(e) : '' });
                return false;
            }
            if (stopped) { hardStop(); return false; }

            /**
             * «start() зарезолвился» ещё не значит «кадры пошли» — и `await
             * video.play()` не способ их дождаться: на камере, которая не отдаёт
             * кадров, её обещание не разрешается никогда (readyState 0, замер
             * 08.10), и сторож первого кадра, взводившийся ПОСЛЕ неё, не
             * взводился: «Включаю камеру» навсегда. Дверь камеры ждёт настоящий
             * кадр; через 6 с видимого времени без него — объяснение, но ожидание
             * продолжается: поздний кадр всё ещё «да».
             */
            var slowSaid = false;
            var framed = await cam.show(video, stream, {
                ui: false,
                // Через 6 с без кадра — сказать своими словами и ждать дальше:
                // живая камера бывает медленнее (≥ 6 с у ~0,5 % запусков).
                onSlow: function () {
                    slowSaid = true;
                    showFail(t('cam_fail_frames', 'Камера открылась, но кадры не идут — закройте другие приложения, которые её используют'));
                    funnel('camera_no_frames', {});
                }
            });
            if (stopped) { hardStop(); return false; }
            if (!framed) {
                hardStop();
                if (!slowSaid) showFail(t('cam_fail_frames', 'Камера открылась, но кадры не идут — закройте другие приложения, которые её используют'));
                return false;
            }
            if (slowSaid) { clearFail(); funnel('camera_frames_late', {}); }

            running = true;
            offerFlip();
            setState('scan', t('cam_scan', 'Сканирую'));
            funnel('camera_started', { facing: facing });
            loop();
            return true;
        }

        function offerFlip() {
            if (!o.flipBtn || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
            navigator.mediaDevices.enumerateDevices().then(function (list) {
                var n = list.filter(function (d) { return d.kind === 'videoinput'; }).length;
                if (n > 1) o.flipBtn.hidden = false;
            }).catch(function () { });
        }

        function hardStop() {
            running = false;
            if (ctl) { try { ctl.abort(); } catch (_) { } ctl = null; }
            try { if (cam) cam.stop(); } catch (_) { }
            try {
                if (stream) stream.getTracks().forEach(function (tr) { tr.stop(); });
            } catch (_) { }
            stream = null;
            try { video.srcObject = null; } catch (_) { }
            if (octx) octx.clearRect(0, 0, overlay.width, overlay.height);
            lastBoxes = [];
        }

        function stop() { stopped = true; hardStop(); }

        async function flip() {
            facing = facing === 'environment' ? 'user' : 'environment';
            hardStop();
            // Прежний цикл ещё доигрывает свой await; loop() при поднятом
            // `looping` просто выходит, и после смены камеры не осталось бы
            // НИ ОДНОГО прохода — живой кадр без перевода и без ошибки.
            for (var i = 0; i < 100 && looping; i++) await wait(60);
            stopped = false;
            lastBoxes = [];
            passN = 0;
            await start();
        }

        // ── проход по кадру ──────────────────────────────────────────────

        function grab() {
            var vw = video.videoWidth, vh = video.videoHeight;
            if (!vw || !vh) return null;
            var s = Math.min(1, SCAN_W / vw);
            var c = document.createElement('canvas');
            c.width = Math.max(1, Math.round(vw * s));
            c.height = Math.max(1, Math.round(vh * s));
            if (!window.__drawCameraFrame(c.getContext('2d'), video, 0, 0, c.width, c.height)) return null;   // no frame this pass
            return c;
        }

        async function translateAll(texts, src, tgt) {
            var need = [], idx = [];
            texts.forEach(function (x, i) {
                if (cache.has(x)) return;
                need.push(x); idx.push(i);
            });
            if (need.length) {
                ctl = new AbortController();
                for (var i = 0; i < need.length; i++) {
                    if (stopped) break;
                    try {
                        var r = await root.TranslatorEngine.translateEntryText(need[i], src, tgt, ctl.signal, {});
                        var out = (r && typeof r === 'object') ? (r.text || '') : String(r || '');
                        if (cache.size > MAX_CACHE) cache.clear();
                        cache.set(need[i], out || need[i]);
                    } catch (e) {
                        if (e && e.name === 'AbortError') break;
                        cache.set(need[i], need[i]);
                    }
                }
                ctl = null;
            }
            return texts.map(function (x) { return cache.get(x) || x; });
        }

        async function pass() {
            var frame = grab();
            if (!frame) return false;
            clearFail();

            var srcWanted = o.srcLang();
            // «Определить автоматически» до решения читает ДВУМЯ распознавателями
            // за один проход детектора (латиница + кириллица), как и снимок, —
            // см. photo-translate/script-pick.js. После решения — одним.
            var P = root.PTScriptPick;
            var dualAuto = srcWanted === 'auto' && !lockedLang && !!P;
            var lang = srcWanted === 'auto' ? (lockedLang || (dualAuto ? 'auto' : 'en')) : srcWanted;

            // Часы идут ПОСЛЕ готовности движка. Первое включение камеры
            // качает 12–20 МБ весов, и если считать это проходом, непрерывный
            // режим выключался бы у ВСЕХ на первом же кадре — по стоимости
            // загрузки, которая больше никогда не повторится.
            var eng = await o.ensureEngine(lang);
            var t0 = performance.now();
            if (stopped) return false;
            var bmp = await createImageBitmap(frame);
            var res = await eng.read(bmp, {});
            if (stopped) return false;
            var merged = dualAuto ? P.mergeDual(res.lines) : null;
            if (merged) res.lines = merged.lines;

            if (!res.lines.length) {
                lastBoxes = [];
                draw(frame.width, frame.height);
                setState('hold', t('cam_hold', 'Наведите на надпись'));
                return performance.now() - t0 > SLOW_MS;
            }

            var blocks = root.PTCompose.groupBlocks(res.lines);

            // Этот проход читал иностранной письменностью, которую проверяем.
            // Принимаем её только по знакам ЕЁ СОБСТВЕННОЙ письменности (латиницу
            // выдают все словари); иначе — следующая, а исчерпав их — латиница.
            if (srcWanted === 'auto' && probing && P) {
                if (P.foreignEvidence(res.lines, root.PPOCR.scriptFor(probing)).ok) {
                    funnel('camera_lang_locked', { lang: probing, foreign: 1 });
                    if (o.onLangLocked) o.onLangLocked(probing);
                    probing = null;
                } else {
                    var nextI = P.FOREIGN.indexOf(probing) + 1;
                    probing = nextI < P.FOREIGN.length ? P.FOREIGN[nextI] : null;
                    lockedLang = probing || 'en';
                    if (!probing) funnel('camera_lang_locked', { lang: 'en', foreign: 0 });
                    return performance.now() - t0 > SLOW_MS;
                }
            }

            // Язык определяем один раз: смена распознавателя закрывает воркер,
            // которым идёт чтение, и на каждом кадре это был бы разрыв.
            if (srcWanted === 'auto' && !lockedLang) {
                if (merged && merged.weak) {
                    // Ни латиница, ни кириллица не прочитали ничего убедительного:
                    // следующий проход читает первой из иностранных письменностей.
                    probing = lockedLang = P.FOREIGN[0];
                    return performance.now() - t0 > SLOW_MS;
                }
                var script = merged && merged.cyrillic ? 'eslav' : 'latin';
                var sample = blocks.slice(0, 6).map(function (b) { return b.text; }).join(' ').slice(0, 400);
                var guess = script === 'eslav' ? 'ru' : 'en';
                try {
                    // {lang, ambiguous}: до 16.09.2026 здесь читалось d.language,
                    // которого не бывает, и ответ определителя выбрасывался.
                    var d = await root.TranslatorEngine.detectLanguageAsync(sample);
                    if (d && d.lang && !d.ambiguous && root.PPOCR.scriptFor(d.lang) === script) guess = d.lang;
                } catch (_) { }
                lockedLang = guess;
                funnel('camera_lang_locked', { lang: guess });
                if (o.onLangLocked) o.onLangLocked(guess);
            }

            var tgt = o.tgtLang();
            var srcCode = srcWanted === 'auto' ? (lockedLang || lang) : lang;
            if (srcCode !== tgt) {
                var outs = await translateAll(blocks.map(function (b) { return b.text; }), srcCode, tgt);
                blocks.forEach(function (b, i) { b.translated = outs[i]; });
            } else {
                blocks.forEach(function (b) { b.translated = b.text; });
            }
            if (stopped) return false;

            lastBoxes = blocks;
            draw(frame.width, frame.height);
            setState('scan', t('cam_scan', 'Сканирую'));
            var ms = performance.now() - t0;
            // Проход идёт примерно раз в секунду; событие на каждый залило бы
            // аналитику одним тулом. Первый показывает, что лейн жив и с каким
            // провайдером, дальше — редкая выборка для темпа.
            if (passN === 0 || passN % 20 === 0) {
                funnel('camera_pass', {
                    ms: Math.round(ms), blocks: blocks.length,
                    provider: eng.provider, n: passN,
                    dual: res.dual ? 1 : 0, mob: MOB,
                });
            }
            passN++;
            return ms > SLOW_MS;
        }

        async function loop() {
            if (looping) return;
            looping = true;
            try {
                while (running && !stopped) {
                    var slow = false;
                    try { slow = await pass(); }
                    catch (e) {
                        if (stopped) break;
                        funnel('camera_pass_failed', { message: String(e && e.message || e).slice(0, 100) });
                        await wait(900);
                    }
                    if (!auto) break;
                    if (slow) {
                        // Непрерывный режим на этом устройстве дороже, чем полезен.
                        auto = false;
                        if (o.scanBtn) o.scanBtn.hidden = false;
                        setState('hold', t('cam_slow', 'Здесь это медленно — сканирую по кнопке'));
                        funnel('camera_slow_manual', {});
                        break;
                    }
                    await wait(120);
                }
            } finally { looping = false; }
        }

        function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

        /** Один проход по требованию — ручной режим на медленном устройстве. */
        async function scanOnce() {
            if (looping || !running) return;
            looping = true;
            setState('warm', t('cam_warm', 'Читаю кадр'));
            try { await pass(); }
            catch (e) { funnel('camera_pass_failed', { message: String(e && e.message || e).slice(0, 100) }); }
            finally { looping = false; setState('hold', t('cam_hold', 'Наведите на надпись')); }
        }

        // ── отрисовка перевода поверх кадра ──────────────────────────────

        /**
         * Перевод рисуется ПЛАШКОЙ поверх надписи, а не вписывается в неё.
         *
         * В снимке мы затираем оригинал по цвету фона и набираем перевод на его
         * месте — это стоит нескольких getImageData на блок и оправдано один
         * раз. В живом кадре тот же приём пришлось бы делать несколько раз в
         * секунду по дрожащему кадру, и он выглядел бы мерцанием. Плашка
         * читается при любом фоне и не притворяется частью сцены.
         */
        function draw(fw, fh) {
            if (!octx) return;
            lastFrame = { w: fw, h: fh };
            var box = host.getBoundingClientRect();
            var vr = video.getBoundingClientRect();
            var dpr = Math.min(2, root.devicePixelRatio || 1);
            if (overlay.width !== Math.round(box.width * dpr) || overlay.height !== Math.round(box.height * dpr)) {
                overlay.width = Math.max(1, Math.round(box.width * dpr));
                overlay.height = Math.max(1, Math.round(box.height * dpr));
            }
            octx.setTransform(dpr, 0, 0, dpr, 0, 0);
            octx.clearRect(0, 0, box.width, box.height);
            if (!lastBoxes.length || !vr.width) return;

            // Кадр распознавания → отображаемый прямоугольник видео → контейнер.
            var sx = vr.width / fw, sy = vr.height / fh;
            var ox = vr.left - box.left, oy = vr.top - box.top;

            octx.textBaseline = 'middle';
            lastBoxes.forEach(function (b) {
                var text = b.translated || b.text;
                if (!String(text).trim()) return;
                var x = ox + b.box.x * sx, y = oy + b.box.y * sy;
                var w = b.box.w * sx, h = b.box.h * sy;
                if (w < 8 || h < 6) return;

                var size = Math.max(11, Math.min(h * 0.82, 30));
                octx.font = '600 ' + size + 'px "Noto Sans", "Segoe UI", Arial, sans-serif';
                var tw = octx.measureText(text).width;
                var padX = 6;
                var bw = Math.min(Math.max(w, tw + padX * 2), box.width - x - 4);
                // Строка длиннее плашки — сжимаем по горизонтали, а не выпускаем наружу.
                var squeeze = tw + padX * 2 > bw ? (bw - padX * 2) / tw : 1;

                var r = Math.min(6, h / 2);
                octx.fillStyle = 'rgba(12,14,18,.84)';
                octx.beginPath();
                if (octx.roundRect) octx.roundRect(x, y, bw, h, r);
                else octx.rect(x, y, bw, h);
                octx.fill();

                octx.save();
                octx.beginPath();
                octx.rect(x, y, bw, h);
                octx.clip();
                octx.fillStyle = '#fff';
                if (squeeze < 1) {
                    octx.translate(x + padX, 0);
                    octx.scale(squeeze, 1);
                    octx.translate(-(x + padX), 0);
                }
                octx.fillText(text, x + padX, y + h / 2);
                octx.restore();
            });
        }

        /**
         * Снять кадр в ПОЛНОМ разрешении и отдать его обычному пути тула.
         *
         * Именно здесь живое наведение превращается в файл, который можно
         * править и сохранить, — то, чего камерные переводчики не дают.
         */
        function shoot() {
            var vw = video.videoWidth, vh = video.videoHeight;
            if (!vw || !vh) return null;
            var c = document.createElement('canvas');
            c.width = vw; c.height = vh;
            if (!window.__drawCameraFrame(c.getContext('2d'), video, 0, 0)) return null;   // an interrupted camera
            return new Promise(function (res) {
                c.toBlob(function (blob) {
                    if (!blob) { res(null); return; }
                    var name = 'camera-' + new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-') + '.jpg';
                    try { res(new File([blob], name, { type: 'image/jpeg' })); }
                    catch (_) { blob.name = name; res(blob); }
                }, 'image/jpeg', 0.92);
            });
        }

        function resetLang() { lockedLang = null; probing = null; cache.clear(); }

        /**
         * Дождаться, пока текущий проход доиграет.
         *
         * Снимок кадра уходит в обычный путь тула, а тот открывает ТОТ ЖЕ
         * движок распознавания. Если проход камеры ещё держит воркер, два
         * чтения встают в очередь к одному worker'у, и первый кадр снимка
         * ждёт конца ненужного уже прохода по живому видео. Ждать здесь
         * дешевле, чем разбираться в этом потом по «иногда долго думает».
         */
        async function idle(maxMs) {
            var end = Date.now() + (maxMs || 6000);
            while (looping && Date.now() < end) await wait(60);
        }

        return {
            start: start, stop: stop, flip: flip, shoot: shoot, idle: idle,
            scanOnce: scanOnce, resetLang: resetLang,
            redraw: function () { if (lastBoxes.length) draw(lastFrame.w, lastFrame.h); },
            isRunning: function () { return running; },
        };
    }

    root.PTCamera = { create: create };
})(window);
